import React, { useState } from 'react';
import { FieldPath, FieldValues, useFormContext } from 'react-hook-form';

import { RowButton } from '@eika/button';
import { Icon, IconTypes } from '@eika/icon';
import { SubModal } from '@eika/modal';

import { useWatchError } from '@/domain/app';
import useText from '@/hooks/use-text';
import DEFAULT_APP_NAMESPACE from '@/utils/modal';

import { Note } from './Note';
import { NoteFieldSummary } from './NoteFieldSummary';
import { useOcrCheck } from './useOcrCheck';

interface Props<T extends FieldValues> {
    locked?: boolean;
    maxLength?: number;
    fieldName: FieldPath<T>;
    label: string;
    placeholder: string;
}

export const NoteField = <T extends FieldValues>({
    locked = false,
    maxLength,
    fieldName,
    label,
    placeholder,
}: Props<T>) => {
    const text = useText();
    const [showModal, setShowModal] = useState(false);
    useOcrCheck();

    const methods = useFormContext<T>();
    const kid = methods.watch(fieldName);
    const error = useWatchError<T>(fieldName, 'pay.validation.kid.illegalLength');

    const triggerModal = (show: boolean) => {
        setShowModal(show);
    };

    return (
        <>
            <RowButton onClick={() => triggerModal(true)} disabled={locked}>
                <NoteFieldSummary
                    label={label}
                    placeholder={placeholder}
                    note={kid}
                    error={error}
                    icon={
                        locked ? (
                            <Icon type={IconTypes.PIN_KODE_24} ariaLabel="Låst" />
                        ) : (
                            <Icon type={IconTypes.LITEN_PIL_HOEYRE} className="icon--20" />
                        )
                    }
                />
            </RowButton>
            <SubModal
                appNamespace={DEFAULT_APP_NAMESPACE}
                id="noteField"
                onClose={() => triggerModal(false)}
                title={text.transferNoteTitle}
                setOpen={() => triggerModal(false)}
                open={showModal}
            >
                <Note
                    onClose={() => {
                        setShowModal(false);
                    }}
                    maxLength={maxLength}
                    fieldName={fieldName}
                />
            </SubModal>
        </>
    );
};
